import React from "react";
import { useReducedMotion } from "framer-motion";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { TrendingUp } from "lucide-react";
import { Panel, CHART, monthLabel, Skeleton } from "./primitives";

const PERIOD_LABEL = { this_week: "This Week", this_month: "This Month", qtd: "Quarter to Date",
  this_year: "Year to Date", last_12: "Last 12 Months", all: "All Time" };

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs" data-testid="perf-tooltip">
      <p className="font-semibold text-slate-700 mb-1">{monthLabel(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="ml-auto font-semibold text-slate-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export function PerformanceChart({ data, loading, index, interviewMonths }) {
  const reduce = useReducedMotion();
  const ivMap = interviewMonths ? Object.fromEntries(interviewMonths.map((m) => [m.month, m.interviews])) : null;
  const series = (data?.trend?.series || []).map((s) => (ivMap ? { ...s, interviews: ivMap[s.month] || 0 } : s));
  const allZero = series.every((s) => !s.new_candidates && !s.interviews);
  const peak = Math.max(0, ...series.map((s) => Math.max(s.new_candidates || 0, s.interviews || 0)));
  const chartH = allZero ? "h-32" : peak <= 3 ? "h-44" : "h-64";   // adaptive: less whitespace on low data
  return (
    <Panel title={`Recruiting Performance — ${PERIOD_LABEL[data?.range] || "Year to Date"}`} icon={TrendingUp} testId="performance-chart" index={index}>
      {loading && !data ? <Skeleton h="h-64" /> : (
        <>
          <div className={`${chartH} w-full`} data-testid="performance-chart-body">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.blue} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={CHART.blue} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={34} />
                <Tooltip content={<TooltipBox />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                <Area type="monotone" dataKey="new_candidates" name="New Candidates" stroke={CHART.blue} strokeWidth={2.5} fill="url(#gNew)" dot={{ r: 2.5, strokeWidth: 0, fill: CHART.blue }} activeDot={{ r: 4 }} isAnimationActive={!reduce} animationDuration={900} />
                <Line type="monotone" dataKey="interviews" name="Interviews" stroke={CHART.cyan} strokeWidth={2.5} dot={{ r: 2.5, strokeWidth: 0, fill: CHART.cyan }} activeDot={{ r: 4 }} isAnimationActive={!reduce} animationDuration={900} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {allZero && <p className="text-xs text-slate-400 text-center mt-1" data-testid="performance-zero-note">No recruiting activity recorded yet for this year.</p>}
        </>
      )}
    </Panel>
  );
}
